import { useSessionBadge } from '../state/workflowProgressStore';
import type { RunBadge } from '../state/workflowProgressStore';

/**
 * WorkflowStatusIcon — the Agent Runs badge (agent-runs-visualization.md §4).
 *
 * Renderer-only: it reads ONE pure selector (`useSessionBadge`) and owns zero state.
 * The badge priority (attention > running > done > idle) lives in the store's
 * `selectSessionBadge`, NOT here — a component that re-derived it would be a second
 * predicate free to drift from the first (the recurring predicate-drift class).
 *
 * `idle` renders NOTHING. The composer cluster is scarce real estate, and a
 * permanently-lit icon for a feature most sessions never use is noise — same gating
 * idiom as the browser icon. So the icon appearing IS the signal that a run exists.
 *
 * Firewall-neutral (`components/`): the mobile cluster row and the desktop topbar
 * both mount it. It must never import from `mobile/` or `desktop/`.
 */

/** Emoji per badge. Deliberately ONE glyph — the dot overlay carries the state. */
const ICON = '\u{1F500}'; // 🔀 twisted arrows — a fan-out, not a spinner

const TITLE: Record<Exclude<RunBadge, 'idle'>, string> = {
  running: 'Agent runs in progress',
  attention: 'An agent run needs attention',
  done: 'An agent run finished',
};

export function WorkflowStatusIcon({
  sessionId,
  onOpen,
}: {
  sessionId: string | null;
  onOpen: () => void;
}) {
  const badge = useSessionBadge(sessionId);

  // idle ⇒ no runs for this session ⇒ no icon at all.
  if (badge === 'idle') return null;

  return (
    <button
      type="button"
      class={`ccl-icon-btn ccl-runs-btn ccl-runs--${badge}`}
      onClick={onOpen}
      title={TITLE[badge]}
      aria-label={TITLE[badge]}
    >
      {ICON}
      {/* The state dot. `running` pulses (amber); `attention` is a static red; `done`
          is a static emerald that clears when the panel opens (markSeen). */}
      <span class={`ccl-runs-dot ccl-runs-dot--${badge}`} aria-hidden="true" />
    </button>
  );
}
